import { useQuery } from '@tanstack/react-query';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { nanoid } from 'nanoid';
import { useMemo, useRef, useState } from 'react';
import { Scatter } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { EditIcon } from '@/components/common/edit-icon';
import { useModal } from '@/components/common/modal';
import { PageLoadingSpinner } from '@/components/common/page-loading-spinner';
import { Switch } from '@/components/common/switch';
import { Button, ButtonVariant } from '@/components/form/button';
import { Input } from '@/components/form/input';
import { MultiListBox } from '@/components/form/multi-list-box';
import { SignedInMethod, useAuth } from '@/features/auth';
import { AnalysisPage } from '@/features/single-measurement';
import { useExport } from '@/hooks/use-export';
import { useRoute } from '@/hooks/use-route';
import { MeasurementOverviewLayout } from '@/layouts';
import { getAdminGroup } from '@/utils/get-admin-group';
import { isResultReady } from '@/utils/is-result-ready';

import { EditFermentationTitleModel } from '../../components/edit-fermentation-title-modal';
import {
  getFavouriteFermentations,
  getFermentation,
  getFermentationBatches,
} from '../../services/fermentations';
import { FermentationQueryKeys } from '../../services/query-keys';
import { fermentationGraphDataMapper, fermentationGraphOptions } from '../../utils/graph';
import { BatchTabGroup } from './components/batch-tab-group';
import { GraphAnalysisList } from './components/graph-analysis-list';
import { TagList } from './components/tag-list';
import { FermentationTopBar } from './components/top-bar';
import { WarningsList } from './components/warnings-list';

ChartJS.register(
  Title,
  Legend,
  Tooltip,
  LineElement,
  LinearScale,
  PointElement,
  CategoryScale,
  ChartDataLabels,
);

const getFermentationMethod = (method: string): 'FW' | 'BB' | 'ETOH' => {
  switch (method) {
    case SignedInMethod.Wine:
      return 'FW';
    case SignedInMethod.Beer:
      return 'BB';
    case SignedInMethod.Ethanol:
      return 'ETOH';
  }
  return 'FW';
};

export const FermentationPage = () => {
  const { fermentationId = '', batchId = '' } = useParams();
  const [selectedFermentation, setSelectedFermentation] = useState<
    { label: string; value: string }[]
  >([]);
  const chartRef = useRef<any>(null);
  const { user } = useAuth();
  const { t } = useTranslation();
  const { toggle, isOpen } = useModal();
  const { routeToFermentationTracking, routeToFermentation, routeToFermentationBatch } =
    useRoute();
  const [graphBoundary, setGraphBoundary] = useState(5);
  const [graphRefreshKey, setGraphRefreshKey] = useState(nanoid());
  const [enableGraphBoundary, setEnableGraphBoundary] = useState(false);

  // generate a new key for batch refresh
  // this is used to force the component to re-render when a batch is added or removed
  const [batchRefreshKey, setBatchRefreshKey] = useState(nanoid());

  // export analyses hook
  const {
    isReportGenerating,
    generateExportZipBundle,
    isExporting,
    generateFermentationPDFReport,
  } = useExport();

  // get fermentation data
  const {
    isLoading,
    isFetched,
    data,
    refetch: refetchFermentation,
  } = useQuery({
    queryKey: [FermentationQueryKeys.GetAFermentations, fermentationId],
    queryFn: () => getFermentation(fermentationId),
    refetchOnWindowFocus: false,
  });

  // get admin role
  const group = useMemo(() => getAdminGroup(user?.roles), [user]);

  // get all the batches in fermentation
  const {
    refetch,
    data: batchData,
    isLoading: batchesIsLoading,
  } = useQuery({
    queryKey: [FermentationQueryKeys.GetBatchesOfFermentation, fermentationId],
    queryFn: () => getFermentationBatches(fermentationId, group),
    refetchOnWindowFocus: false,
  });

  // result ready batches
  const batches = useMemo(
    () => batchData?.filter((batch) => isResultReady(batch.state)).reverse(),
    [batchData],
  );

  // graph data set
  const datasets = useMemo(
    () =>
      fermentationGraphDataMapper({
        batches,
        method: data?.method,
        fermentationName: data?.name,
      }),
    [batches, data?.method],
  );

  // get favourite fermentations
  const { data: favouriteFermentations } = useQuery({
    queryKey: [FermentationQueryKeys.GetFavouriteFermentations],
    queryFn: () =>
      getFavouriteFermentations(getFermentationMethod(user?.signedInMethod ?? '')),
    refetchOnWindowFocus: false,
  });

  // get favourite fermentation batches
  const { data: selectedFermentationBatches } = useQuery({
    queryKey: [
      FermentationQueryKeys.GetFavouriteFermentations,
      selectedFermentation.map(({ value: id }) => id),
    ],
    queryFn: async () => {
      const allBatches = await Promise.all(
        selectedFermentation.map(({ value: id }) => getFermentationBatches(id, group)),
      );
      return allBatches.flat().filter((batch) => isResultReady(batch.state));
    },
    enabled: selectedFermentation.map(({ value: id }) => id).length > 0,
    refetchOnWindowFocus: false,
  });

  const selectedFermentationDatasets = useMemo(() => {
    if (!selectedFermentationBatches || selectedFermentationBatches.length === 0)
      return [];
    const selectedFermentationId = selectedFermentationBatches[0].parent;
    const selectedFermentationName = selectedFermentation.find(
      ({ value }) => value === selectedFermentationId,
    )?.label;
    return fermentationGraphDataMapper({
      graphBoundary,
      enableGraphBoundary,
      isReferenceCurve: true,
      method: data?.method,
      batches: selectedFermentationBatches,
      fermentationName: selectedFermentationName,
    }).datasets.map((dataset: any) => ({ ...dataset }));
  }, [selectedFermentationBatches, data?.method, graphBoundary, enableGraphBoundary]);

  const graphDatasets = useMemo(() => {
    return [...datasets.datasets, ...selectedFermentationDatasets];
  }, [selectedFermentationDatasets, datasets]);

  // favourite fermentation dropdown options
  const favouriteFermentationsOptions = useMemo(() => {
    if (favouriteFermentations && favouriteFermentations.length > 0) {
      return favouriteFermentations.map((fermentation) => ({
        label: fermentation.name,
        value: fermentation.id,
      }));
    }
    return [
      {
        label: t('fermentations.comparison.noFavourite'),
        value: '',
      },
    ];
  }, [selectedFermentation, favouriteFermentations]);

  const handleCoridorChange = (state: boolean) => {
    if (chartRef.current === null) return;
    setGraphRefreshKey(nanoid());
    setEnableGraphBoundary(state);
  };

  // generate report
  const generateReport = () => {
    const chart = chartRef.current;
    if (chart && batches) {
      const image = chart.toBase64Image('image/png');
      generateFermentationPDFReport({ batches, tags: data?.tags, image });
    }
  };

  const exportReportAsZip = () => {
    if (batches) generateExportZipBundle(batches);
  };

  const onBatchesUpdate = () => {
    refetch();
    if (batchId) {
      routeToFermentationBatch({ batchId, fermentationId });
    } else {
      routeToFermentation(fermentationId);
    }
    setBatchRefreshKey(nanoid());
  };

  return (
    <MeasurementOverviewLayout>
      {!batchId && <FermentationTopBar />}

      {isFetched ? (
        <MeasurementOverviewLayout.Title title={data?.name ?? ''}>
          <div className="flex items-center justify-between grow">
            <Button
              id="step-edit-analysis"
              variant={ButtonVariant.Link}
              classnames="flex items-center gap-2"
              onClick={toggle}
            >
              <EditIcon color="bg-black-100" />
            </Button>

            {!isLoading && isFetched ? (
              <div className="flex gap-4">
                <Button
                  id="step-download-pdf"
                  variant={ButtonVariant.Secondary}
                  disabled={!isFetched}
                  isLoading={isReportGenerating}
                  onClick={generateReport}
                >
                  {t('PDF Report')}
                </Button>

                <Button
                  variant={ButtonVariant.Secondary}
                  disabled={!isFetched}
                  isLoading={isExporting}
                  onClick={exportReportAsZip}
                >
                  {t('CSV Export')}
                </Button>
              </div>
            ) : null}
          </div>
        </MeasurementOverviewLayout.Title>
      ) : null}

      <MeasurementOverviewLayout.Body>
        {isLoading ? <PageLoadingSpinner /> : null}

        {!isLoading && isFetched ? (
          <div className="flex flex-col w-full gap-5 -mb-40">
            <GraphAnalysisList batches={batches} onListUpdate={refetch} />

            <WarningsList batches={batches} />

            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="md:w-1/2">
                  <MultiListBox
                    options={favouriteFermentationsOptions}
                    onChange={(selected) => {
                      setSelectedFermentation(selected);
                    }}
                    selectedValues={selectedFermentation}
                    placeholder={t('fermentations.comparison.placeholder')}
                  />
                </div>

                {selectedFermentation.length > 0 ? (
                  <div className="flex items-center gap-5">
                    {enableGraphBoundary ? (
                      <div className="flex items-center gap-3">
                        <label
                          className="whitespace-nowrap"
                          htmlFor="graphBoundaryThreshold"
                        >
                          Corridor Range [%]
                        </label>
                        <Input
                          className="w-20"
                          type="number"
                          value={graphBoundary}
                          onChange={(e) => setGraphBoundary(parseInt(e.target.value))}
                          name="graphBoundaryThreshold"
                          min={1}
                          max={100}
                        />
                      </div>
                    ) : null}
                    <div className="flex items-center gap-3">
                      <label
                        className="whitespace-nowrap"
                        htmlFor="graphBoundaryThreshold"
                      >
                        Show as Corridors
                      </label>
                      <Switch
                        isToggled={enableGraphBoundary}
                        onToggle={handleCoridorChange}
                      />
                    </div>
                  </div>
                ) : null}
              </div>

              <Scatter
                options={fermentationGraphOptions}
                data={{ datasets: graphDatasets }}
                ref={chartRef}
                key={graphRefreshKey}
              />
            </div>

            <div className="py-10">
              <TagList tags={data?.tags} />
            </div>

            <BatchTabGroup
              key={batchRefreshKey}
              batches={batches}
              isLoading={batchesIsLoading}
              fermentationId={fermentationId}
            />

            <EditFermentationTitleModel
              isOpen={isOpen}
              toggle={toggle}
              data={data}
              onSuccess={refetchFermentation}
            />
          </div>
        ) : null}
      </MeasurementOverviewLayout.Body>

      {batchId && (
        <AnalysisPage
          key={batchId}
          backButton={routeToFermentationTracking}
          onFermentationChange={onBatchesUpdate}
        />
      )}
    </MeasurementOverviewLayout>
  );
};
